import { Progress, Table, TableColumn } from "@backstage/core-components";
import { JiraActionItemsPayload } from "@internal/plugin-ops-review-common";
import { Spacer } from "@internal/plugin-utilities";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Typography,
} from "@material-ui/core";
import ExpandMore from "@material-ui/icons/ExpandMore";
import React from "react";
import { InformationTooltip } from "../InformationTooltip";
import { SectionHeader } from "../TeamBreakdown/SectionHeader";

interface Props {
  loading: boolean;
  data: any;
}

export const JiraIssuesSection: React.FC<Props> = ({
  loading,
  data,
}): JSX.Element => {
  const [expanded, setExpanded] = React.useState<string | false>("panel1");

  const handleAccordionChange =
    (panel: string) => (_event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const tooltip = (
    <>
      <Typography variant="h5">Action Items</Typography>
      <div>
        This section will display Jira issues tagged with{" "}
        <code>
          opsreview, opsreview-exceptions, opsreview-queries, opsreview-specs,
          opsreview-security, opsreview-postmortem
        </code>{" "}
        that <strong>do not</strong> have a status of{" "}
        <code>
          archived, closed, done, canceled, merged, Deployed To Production
        </code>
        .
      </div>
    </>
  );

  const header = (
    <div style={{ textAlign: "center" }}>
      <SectionHeader vendor="jira" title="Action Items" />
      <InformationTooltip title={tooltip} />
      <Spacer variant="normal" />
    </div>
  );

  if (loading) {
    return (
      <>
        {header}
        <Progress />
      </>
    );
  }

  const highPriority: any[] = [];
  const lowPriority: any[] = [];

  data?.forEach((item: any) => {
    const name = item.fields.priority.name.toLowerCase();
    if (["critical", "high"].includes(name)) {
      highPriority.push(item);
      return;
    }
    lowPriority.push(item);
  });

  const columns: TableColumn<JiraActionItemsPayload>[] = [
    {
      title: "Key",
      field: "key",
      highlight: true,
      width: "10%",
      render: (rowData) => {
        return (
          <a
            href={`https://kajabi.atlassian.net/browse/${rowData.key}`}
            title={rowData.key}
            target="_blank"
          >
            {rowData.key}
          </a>
        );
      },
    },
    {
      title: "Status",
      field: "status",
      highlight: true,
      width: "5%",
      render: (rowData) => {
        const fields = rowData.fields;
        const backgroundColor = fields.status.statusCategory.colorName;
        const color = backgroundColor === "yellow" ? "black" : "white";

        return (
          <Chip
            style={{ backgroundColor, color }}
            label={fields.status.name}
            size="small"
          />
        );
      },
    },
    {
      title: "Summary",
      field: "summary",
      highlight: true,
      width: "30%",
      render: (rowData) => {
        const fields = rowData.fields;
        return (
          <a
            href={`https://kajabi.atlassian.net/browse/${rowData.key}`}
            title={rowData.key}
            target="_blank"
          >
            {fields.summary}
          </a>
        );
      },
    },
    {
      title: "Created",
      field: "fields.created",
      type: "datetime",
      highlight: true,
      width: "10%",
      dateSetting: { locale: "en-US" },
    },
    {
      title: "Priority",
      field: "priority",
      highlight: true,
      width: "10%",
      render: (rowData) => {
        const fields = rowData.fields;
        return fields.priority.name;
      },
    },
    {
      title: "Assignee",
      field: "assignee",
      highlight: true,
      width: "20%",
      render: (rowData) => {
        const fields = rowData.fields;
        if (fields.assignee === null) {
          return "Unassigned";
        }

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <img
              style={{ border: "1px solid white", borderRadius: "10px" }}
              src={fields.assignee.avatarUrls["48x48"]}
              alt={fields.assignee.displayName}
              height="48px"
              width="48px"
            />{" "}
            {fields.assignee.displayName}
          </div>
        );
      },
    },
    {
      title: "Tags",
      field: "tags",
      highlight: true,
      render: (rowData) => {
        const fields = rowData.fields;
        return fields.labels.map((label: string) => {
          return <Chip label={label} size="small" />;
        });
      },
    },
  ];

  return (
    <div>
      {header}
      <Accordion
        defaultExpanded
        expanded={expanded === "panel1"}
        onChange={handleAccordionChange("panel1")}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h3" style={{ width: "100%" }} align="center">
            🚨 High Priority Items
          </Typography>
        </AccordionSummary>
        <AccordionDetails style={{ width: "100%", display: "block" }}>
          <Table
            options={{
              sorting: true,
              search: true,
              paging: true,
              pageSize: 5,
            }}
            columns={columns}
            data={highPriority || []}
            style={{
              width: "100%",
              flex: "0 0 100%",
              fontSize: "1.1em",
            }}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === "panel2"}
        onChange={handleAccordionChange("panel2")}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h3" align="center" style={{ width: "100%" }}>
            ⚠️ Lower Priority Items
          </Typography>
        </AccordionSummary>
        <AccordionDetails style={{ width: "100%", display: "block" }}>
          <Table
            options={{
              sorting: true,
              search: true,
              paging: true,
              pageSize: 5,
            }}
            columns={columns}
            data={lowPriority || []}
            style={{
              width: "100%",
              flex: "0 0 100%",
              fontSize: "1.1em",
            }}
          />
        </AccordionDetails>
      </Accordion>
    </div>
  );
};
